import GuideArticle from '../components/GuideArticle'
import type { RelatedCalc, HowToStep } from '../components/GuideArticle'
import type { QA } from '../components/Faq'
import { useLocale, localizedHref } from '../i18n/locale'

interface Content {
  title: string
  h1: string
  description: string
  category: string
  faqs: QA[]
  related: RelatedCalc[]
  // Step-by-step data for HowTo schema. Mirrors the visible <ol> in the body
  // but as plain text so Google's rich result can render the step cards.
  steps: HowToStep[]
}

const STEPS_EN: HowToStep[] = [
  { name: 'Calculate the volume', text: 'Work out cubic feet or yards (or bags) so you can place the whole pour in one continuous go — cold joints between batches are weak points.' },
  { name: 'Prep the base', text: 'Excavate, add and compact a gravel base for drainage and support, then build and level your forms.' },
  { name: 'Add reinforcement', text: 'Lay wire mesh or rebar and raise it on chairs so it ends up in the middle of the slab, not on the dirt.' },
  { name: 'Mix the concrete', text: 'Add most of the water, mix to a workable consistency that holds its shape, then add the rest sparingly. Resist the urge to make it soupy.' },
  { name: 'Pour and spread', text: 'Place concrete evenly across the forms, working it into corners and edges and removing air pockets.' },
  { name: 'Screed level', text: 'Drag a straight board across the top of the forms in a sawing motion to strike off the excess and level the surface.' },
  { name: 'Float and finish', text: 'Float to bring up a smooth surface, run an edger along the forms, cut control joints, and add a broom finish for slip resistance if desired.' },
  { name: 'Cure it slowly', text: 'Keep the surface damp (mist it or cover it) for several days. Slow, moist curing is what builds strength.' },
]

const STEPS_FR: HowToStep[] = [
  { name: 'Calculer le volume', text: 'Déterminez les pieds cubes ou verges cubes (ou les sacs) pour pouvoir placer toute la coulée d\'un seul trait continu — les joints froids entre les gâchées sont des points faibles.' },
  { name: 'Préparer le lit', text: 'Excavez, ajoutez et compactez un lit de gravier pour le drainage et le soutien, puis construisez et mettez vos coffrages de niveau.' },
  { name: 'Ajouter l\'armature', text: 'Posez le treillis métallique ou les barres d\'armature et soulevez-les sur des chaises pour qu\'ils se retrouvent au milieu de la dalle, et non sur la terre.' },
  { name: 'Mélanger le béton', text: 'Ajoutez la majeure partie de l\'eau, mélangez jusqu\'à une consistance maniable qui garde sa forme, puis ajoutez le reste avec parcimonie. Résistez à l\'envie de le rendre liquide.' },
  { name: 'Couler et étaler', text: 'Placez le béton uniformément dans les coffrages, en le travaillant dans les coins et les bords et en éliminant les poches d\'air.' },
  { name: 'Régler le niveau', text: 'Faites glisser une règle droite sur le dessus des coffrages dans un mouvement de scie pour araser l\'excédent et mettre la surface de niveau.' },
  { name: 'Talocher et finir', text: 'Talochez pour faire remonter une surface lisse, passez une bordureuse le long des coffrages, taillez les joints de contrôle et ajoutez une finition au balai pour l\'antidérapance au besoin.' },
  { name: 'Faire la cure lentement', text: 'Gardez la surface humide (vaporisez-la ou couvrez-la) pendant plusieurs jours. C\'est une cure lente et humide qui bâtit la résistance.' },
]

const EN: Content = {
  title: 'Concrete Mixing & Pouring Guide: Ratios, Curing & Tips | RenoSheets',
  h1: 'Concrete Mixing & Pouring',
  description:
    'How to mix and pour concrete for slabs, footings and post holes — base prep, mix ratios, water amounts, finishing, and proper curing — plus how much to buy.',
  category: 'Concrete',
  related: [
    { label: 'Concrete Calculator', href: '/calculators/concrete/', blurb: 'Bags or cubic yards for slabs, footings and post holes.' },
    { label: 'Gravel Calculator', href: '/calculators/gravel/', blurb: 'Base material under a slab — yards, tons and bags.' },
  ],
  steps: STEPS_EN,
  faqs: [
    {
      q: 'What is the right ratio for mixing concrete?',
      a: 'A common general-purpose concrete mix is roughly 1 part cement, 2 parts sand, and 3 parts gravel (a 1:2:3 ratio) by volume, with enough water to make a workable but not soupy mix. If you use pre-mixed bagged concrete, the cement, sand, and aggregate are already blended — you just add water per the bag.',
    },
    {
      q: 'How much water do I add to a bag of concrete?',
      a: 'Follow the bag — most 80 lb bags take roughly 3 quarts (about 6 cups) of water, but the exact amount varies by product. Add most of the water, mix, then add the rest sparingly. The mix should hold its shape on a trowel and not run. Too much water is the number-one cause of weak, cracking concrete.',
    },
    {
      q: 'How long does concrete take to cure?',
      a: 'Concrete is usually firm enough to walk on after about 24 to 48 hours, reaches roughly 70% of its strength in about 7 days, and continues curing to full strength over about 28 days. Keep it damp during the first several days — concrete cures through a chemical reaction, not by drying out, so slow, moist curing makes it stronger.',
    },
    {
      q: 'When can I drive on new concrete?',
      a: 'As a general rule, wait about 7 days before light vehicle traffic and the full ~28-day cure before heavy loads, though product and conditions vary. Driving on it too early can crack the slab before it has gained enough strength.',
    },
    {
      q: 'Do I need rebar or wire mesh in concrete?',
      a: 'Reinforcement helps control cracking and adds strength. Welded wire mesh is common in slabs and walkways, while rebar is used in footings, thicker slabs, and load-bearing work. Position reinforcement in the middle of the slab thickness (on chairs), not lying on the ground.',
    },
  ],
}

const FR: Content = {
  title: 'Guide pour mélanger et couler le béton : proportions, cure et astuces | RenoSheets',
  h1: 'Mélanger et couler le béton',
  description:
    'Comment mélanger et couler le béton pour des dalles, des semelles et des trous de poteaux — préparation du lit, proportions du mélange, quantités d’eau, finition et cure adéquate — et combien en acheter.',
  category: 'Béton',
  related: [
    { label: 'Calculateur de béton', href: '/calculators/concrete/', blurb: 'Sacs ou verges cubes pour les dalles, les semelles et les trous de poteaux.' },
    { label: 'Calculateur de gravier', href: '/calculators/gravel/', blurb: 'Matériau de base sous une dalle — verges, tonnes et sacs.' },
  ],
  steps: STEPS_FR,
  faqs: [
    {
      q: 'Quelle est la bonne proportion pour mélanger le béton?',
      a: 'Un mélange de béton tout usage courant est d’environ 1 part de ciment, 2 parts de sable et 3 parts de gravier (un rapport de 1:2:3) en volume, avec assez d’eau pour un mélange maniable mais pas liquide. Si vous utilisez du béton ensaché prémélangé, le ciment, le sable et le granulat sont déjà mélangés — vous n’avez qu’à ajouter l’eau selon le sac.',
    },
    {
      q: 'Combien d’eau faut-il ajouter à un sac de béton?',
      a: 'Suivez le sac — la plupart des sacs de 80 lb prennent environ 3 pintes (à peu près 6 tasses) d’eau, mais la quantité exacte varie selon le produit. Ajoutez la majeure partie de l’eau, mélangez, puis ajoutez le reste avec parcimonie. Le mélange devrait garder sa forme sur une truelle sans couler. Trop d’eau est la cause numéro un d’un béton faible et fissuré.',
    },
    {
      q: 'Combien de temps le béton met-il à faire sa cure?',
      a: 'Le béton est habituellement assez ferme pour qu’on y marche après environ 24 à 48 heures, atteint environ 70 % de sa résistance en environ 7 jours, et poursuit sa cure jusqu’à pleine résistance sur environ 28 jours. Gardez-le humide pendant les premiers jours — le béton fait sa cure par une réaction chimique, et non en séchant, alors une cure lente et humide le rend plus résistant.',
    },
    {
      q: 'Quand puis-je rouler sur du béton neuf?',
      a: 'En règle générale, attendez environ 7 jours avant la circulation de véhicules légers et la cure complète d’environ 28 jours avant les charges lourdes, même si le produit et les conditions varient. Rouler dessus trop tôt peut fissurer la dalle avant qu’elle ait acquis assez de résistance.',
    },
    {
      q: 'Faut-il des barres d’armature ou un treillis métallique dans le béton?',
      a: 'L’armature aide à maîtriser la fissuration et ajoute de la résistance. Le treillis métallique soudé est courant dans les dalles et les allées, tandis que les barres d’armature servent dans les semelles, les dalles plus épaisses et les ouvrages porteurs. Positionnez l’armature au milieu de l’épaisseur de la dalle (sur des chaises), et non posée sur le sol.',
    },
  ],
}

export default function GuideConcreteMixing() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <GuideArticle
      title={c.title}
      h1={c.h1}
      description={c.description}
      path="/guides/concrete-mixing-and-pouring/"
      updated="2026-06-04"
      category={c.category}
      faqs={c.faqs}
      related={c.related}
      howToSteps={c.steps}
    >
      {locale === 'fr' ? (
        <>
          <p>
            Le béton est peu coûteux à acheter et coûteux à refaire. Les deux choses qui déterminent si une coulée dure des
            décennies ou fissure en une saison sont la <strong>teneur en eau</strong> et la <strong>cure</strong> — réussissez-les
            et le reste est simple. Ce guide couvre le mélange et la coulée pour les dalles, les semelles et les trous de poteaux.
          </p>

          <h2>Ce qu’il vous faut</h2>
          <p>
            D’abord, déterminez la quantité de béton qu’exige le travail avec le <a href={L('/calculators/concrete/')}>Calculateur de béton</a>
            {' '}(il donne les sacs ou les verges cubes), et le gravier de base avec le <a href={L('/calculators/gravel/')}>Calculateur de gravier</a>.
            Vous voudrez aussi :
          </p>
          <ul>
            <li><strong>Béton</strong> — prémélange ensaché pour les petits travaux, ou livraison de béton prêt à l’emploi pour les grandes coulées.</li>
            <li><strong>Gravier</strong> pour le lit, plus des planches de coffrage et des piquets pour façonner la coulée.</li>
            <li><strong>Armature</strong> — treillis métallique soudé ou barres d’armature, avec des chaises pour la maintenir au milieu de la dalle.</li>
            <li>Un <strong>bac à mélanger ou une brouette</strong> (ou une bétonnière), une règle à araser, une taloche, une bordureuse et une truelle.</li>
            <li>Des gants et des lunettes de protection — le béton frais est caustique.</li>
          </ul>

          <h2>Étape par étape</h2>
          <ol>
            <li><strong>Calculer le volume.</strong> Déterminez les pieds cubes ou verges cubes (ou les sacs) pour pouvoir placer toute la coulée d’un seul trait continu — les joints froids entre les gâchées sont des points faibles.</li>
            <li><strong>Préparer le lit.</strong> Excavez, ajoutez et compactez un lit de gravier pour le drainage et le soutien, puis construisez et mettez vos coffrages de niveau.</li>
            <li><strong>Ajouter l’armature.</strong> Posez le treillis métallique ou les barres d’armature et soulevez-les sur des chaises pour qu’ils se retrouvent au milieu de la dalle, et non sur la terre.</li>
            <li><strong>Mélanger le béton.</strong> Ajoutez la majeure partie de l’eau, mélangez jusqu’à une consistance maniable qui garde sa forme, puis ajoutez le reste avec parcimonie. Résistez à l’envie de le rendre liquide.</li>
            <li><strong>Couler et étaler.</strong> Placez le béton uniformément dans les coffrages, en le travaillant dans les coins et les bords et en éliminant les poches d’air.</li>
            <li><strong>Régler le niveau.</strong> Faites glisser une règle droite sur le dessus des coffrages dans un mouvement de scie pour araser l’excédent et mettre la surface de niveau.</li>
            <li><strong>Talocher et finir.</strong> Talochez pour faire remonter une surface lisse, passez une bordureuse le long des coffrages, taillez les joints de contrôle et ajoutez une finition au balai pour l’antidérapance au besoin.</li>
            <li><strong>Faire la cure lentement.</strong> Gardez la surface humide (vaporisez-la ou couvrez-la) pendant plusieurs jours. C’est une cure lente et humide qui bâtit la résistance.</li>
          </ol>

          <h2>Calendrier de cure et de résistance</h2>
          <ul>
            <li><strong>~24 à 48 heures :</strong> assez ferme pour qu’on y marche.</li>
            <li><strong>~7 jours :</strong> environ 70 % de la résistance; la circulation légère est généralement acceptable.</li>
            <li><strong>~28 jours :</strong> pleine résistance de calcul; charges lourdes.</li>
            <li>Gardez-le humide pendant les premiers jours — ne le laissez pas sécher rapidement.</li>
          </ul>

          <h2>Erreurs courantes à éviter</h2>
          <ul>
            <li><strong>Trop d’eau</strong> — la cause la plus fréquente d’un béton faible, poussiéreux et fissuré.</li>
            <li>Négliger le lit de gravier ou couler sur un sol non compacté.</li>
            <li>Laisser le béton sécher au lieu de le faire durcir à l’humide.</li>
            <li>Couler par temps de gel ou de canicule sans précautions.</li>
            <li>Omettre les joints de contrôle, de sorte que la dalle fissure où elle veut plutôt qu’où vous l’aviez prévu.</li>
          </ul>
        </>
      ) : (
        <>
          <p>
            Concrete is cheap to buy and expensive to redo. The two things that decide whether a pour lasts decades or cracks
            in a season are the <strong>water content</strong> and the <strong>cure</strong> — get those right and the rest is
            straightforward. This guide covers mixing and pouring for slabs, footings, and post holes.
          </p>

          <h2>What you'll need</h2>
          <p>
            First, figure out how much concrete the job takes with the <a href={L('/calculators/concrete/')}>Concrete Calculator</a>
            (it gives bags or cubic yards), and the base gravel with the <a href={L('/calculators/gravel/')}>Gravel Calculator</a>.
            You'll also want:
          </p>
          <ul>
            <li><strong>Concrete</strong> — bagged pre-mix for small jobs, or ready-mix delivery for large pours.</li>
            <li><strong>Gravel</strong> for the base, plus form boards and stakes to shape the pour.</li>
            <li><strong>Reinforcement</strong> — welded wire mesh or rebar, with chairs to hold it mid-slab.</li>
            <li>A <strong>mixing tub or wheelbarrow</strong> (or a mixer), a screed board, a float, an edger, and a trowel.</li>
            <li>Gloves and eye protection — wet concrete is caustic.</li>
          </ul>

          <h2>Step by step</h2>
          <ol>
            <li><strong>Calculate the volume.</strong> Work out cubic feet/yards (or bags) so you can place the whole pour in one continuous go — cold joints between batches are weak points.</li>
            <li><strong>Prep the base.</strong> Excavate, add and compact a gravel base for drainage and support, then build and level your forms.</li>
            <li><strong>Add reinforcement.</strong> Lay wire mesh or rebar and raise it on chairs so it ends up in the middle of the slab, not on the dirt.</li>
            <li><strong>Mix the concrete.</strong> Add most of the water, mix to a workable consistency that holds its shape, then add the rest sparingly. Resist the urge to make it soupy.</li>
            <li><strong>Pour and spread.</strong> Place concrete evenly across the forms, working it into corners and edges and removing air pockets.</li>
            <li><strong>Screed level.</strong> Drag a straight board across the top of the forms in a sawing motion to strike off the excess and level the surface.</li>
            <li><strong>Float and finish.</strong> Float to bring up a smooth surface, run an edger along the forms, cut control joints, and add a broom finish for slip resistance if desired.</li>
            <li><strong>Cure it slowly.</strong> Keep the surface damp (mist it or cover it) for several days. Slow, moist curing is what builds strength.</li>
          </ol>

          <h2>Curing &amp; strength timeline</h2>
          <ul>
            <li><strong>~24–48 hours:</strong> firm enough to walk on.</li>
            <li><strong>~7 days:</strong> roughly 70% strength; light traffic is generally OK.</li>
            <li><strong>~28 days:</strong> full design strength; heavy loads.</li>
            <li>Keep it moist during the first several days — don't let it dry out fast.</li>
          </ul>

          <h2>Common mistakes to avoid</h2>
          <ul>
            <li><strong>Too much water</strong> — the single most common cause of weak, dusty, cracking concrete.</li>
            <li>Skipping the gravel base or pouring on uncompacted ground.</li>
            <li>Letting concrete dry instead of curing it moist.</li>
            <li>Pouring in freezing or scorching conditions without precautions.</li>
            <li>Leaving out control joints, so the slab cracks where it wants to instead of where you planned.</li>
          </ul>
        </>
      )}
    </GuideArticle>
  )
}
